import AddContentUsageButton from "@/content-usage/buttons/add-button/AddContentUsageButton"
import ContentUsageGridItem, {
  ContentUsageGridItemSkeleton,
} from "@/content-usage/modules/grid/ContentUsageGridItem"
import { useActiveOrganization } from "@/core/context/ActiveOrganizationContext"
import DashboardBlockAdminDropdown from "@/dashboard/blocks/DashboardBlockAdminDropdown"
import DashboardBlockEmptyState from "@/dashboard/blocks/kinds/DashboardBlockEmptystate"
import { CollectionFolderDashboardBlockContentFragment$key } from "@/dashboard/blocks/kinds/__generated__/CollectionFolderDashboardBlockContentFragment.graphql"
import { CollectionFolderDashboardBlockContentPaginationQuery } from "@/dashboard/blocks/kinds/__generated__/CollectionFolderDashboardBlockContentPaginationQuery.graphql"
import { CollectionFolderDashboardBlockContentQuery } from "@/dashboard/blocks/kinds/__generated__/CollectionFolderDashboardBlockContentQuery.graphql"
import { CollectionFolderDashboardBlockContent_PaginationFragment$key } from "@/dashboard/blocks/kinds/__generated__/CollectionFolderDashboardBlockContent_PaginationFragment.graphql"
import { useDashboardBlockCarouselSize } from "@/dashboard/util/useDashboardBlockCarouselSize"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoCarousel, DiscoCarouselSkeleton, DiscoText } from "@disco-ui"
import usePermissions from "@utils/hook/usePermissions"
import {
  useFragment,
  useLazyLoadQuery,
  usePaginationFragment,
  useSubscribeToInvalidationState,
} from "react-relay"
import { graphql } from "relay-runtime"

interface CollectionFolderDashboardBlockContentProps {
  dashboardBlockKey: CollectionFolderDashboardBlockContentFragment$key
  className?: string
}

function CollectionFolderDashboardBlockContent({
  dashboardBlockKey,
  className,
}: CollectionFolderDashboardBlockContentProps) {
  const classes = useStyles()
  const activeOrganization = useActiveOrganization()
  const permissions = usePermissions(activeOrganization)

  const block = useFragment<CollectionFolderDashboardBlockContentFragment$key>(
    graphql`
      fragment CollectionFolderDashboardBlockContentFragment on CollectionFolderDashboardBlock {
        position
        contentId
        ...DashboardBlockAdminDropdownFragment
      }
    `,
    dashboardBlockKey
  )

  const { content } = useLazyLoadQuery<CollectionFolderDashboardBlockContentQuery>(
    graphql`
      query CollectionFolderDashboardBlockContentQuery($id: ID!, $first: Int) {
        content: node(id: $id) {
          ... on Content {
            id
            name
            usages {
              edges {
                node {
                  id
                  collection {
                    app {
                      id
                    }
                  }
                }
              }
            }
            ...CollectionFolderDashboardBlockContent_PaginationFragment
              @arguments(first: $first)
          }
        }
      }
    `,
    { id: block.contentId, first: 3 }
  )

  const {
    data,
    hasPrevious,
    loadNext,
    hasNext,
    isLoadingNext: contentIsLoadingNext,
    refetch,
  } = usePaginationFragment<
    CollectionFolderDashboardBlockContentPaginationQuery,
    CollectionFolderDashboardBlockContent_PaginationFragment$key
  >(
    graphql`
      fragment CollectionFolderDashboardBlockContent_PaginationFragment on Content
      @refetchable(queryName: "CollectionFolderDashboardBlockContentPaginationQuery")
      @argumentDefinitions(first: { type: "Int" }, after: { type: "String" }) {
        children(first: $first, after: $after)
          @connection(key: "CollectionFolderDashboardBlockContent_children") {
          __id
          totalCount
          edges {
            node {
              id
              content {
                name
              }
              ...ContentUsageGridItemFragment
            }
          }
        }
      }
    `,
    content
  )

  const carouselProps = useDashboardBlockCarouselSize(4, block, "center")

  // Listen for invalidation when adding content item
  useSubscribeToInvalidationState(data?.children ? [data?.children?.__id] : [], () => {
    refetch({
      first: 3,
    })
  })

  const contentUsages = Relay.connectionToArray(data?.children)
  const totalCount = data?.children?.totalCount || 0

  const usage = Relay.connectionToArray(content?.usages)[0]
  if (!usage?.collection?.app) return null
  if (totalCount === 0 && !permissions.has("content.manage")) return null

  return (
    <DiscoCarousel
      classes={{
        slide: classes.slide,
        header: classes.header,
      }}
      {...carouselProps}
      title={content?.name}
      moreActions={
        <DashboardBlockAdminDropdown dashboardBlockKey={block} className={className} />
      }
      data={contentUsages}
      item={(contentUsage, i) => (
        <ContentUsageGridItem
          key={contentUsage.id}
          isDashboardBlock
          testid={`CollectionFolderDashboardBlock.item-${i}`}
          contentUsageKey={contentUsage}
          showOverflow={false}
          readOnlyReactionButton
        />
      )}
      testid={"CollectionFolderDashboardBlock"}
      refetch={{
        hasNext,
        hasPrevious,
        loadMore: () => {
          loadNext(3)
        },
        isLoading: contentIsLoadingNext,
      }}
      itemSkeleton={<ContentUsageGridItemSkeleton />}
      totalCount={totalCount}
      emptyState={
        <DashboardBlockEmptyState
          testid={"DashboardBlockEmptyState.collection_folder"}
          title={"No Content in this Folder yet"}
          subtitle={"Create Content in this folder now!"}
          buttons={
            <AddContentUsageButton
              testid={"CollectionFolderDashboardBlockContent.add-content"}
              addingTo={"content"}
              contentUsageInput={{
                entity: "content",
                entityId: content?.id,
                ordering: 0,
              }}
              variant={"text"}
              color={"primary"}
              leftIcon={undefined}
            >
              <DiscoText variant={"body-sm-600"} color={"primary.main"}>
                {"Add Content"}
              </DiscoText>
            </AddContentUsageButton>
          }
        />
      }
    />
  )
}

const useStyles = makeUseStyles(() => ({
  slide: {
    width: "254px",
    height: "260px",
  },
  header: {
    margin: 0,
  },
}))

export function CollectionFolderDashboardBlockContentSkeleton() {
  return (
    <DiscoCarouselSkeleton
      showTitle
      item={<ContentUsageGridItemSkeleton />}
      slidesPerView={3}
    />
  )
}

export default Relay.withSkeleton({
  component: CollectionFolderDashboardBlockContent,
  skeleton: CollectionFolderDashboardBlockContentSkeleton,
})
